/**
 * SNS share helpers for #419 / #69 (two-tier share).
 *
 * Two tiers of spreading (#69):
 *   1. Official-post amplification — the store curates one post per platform
 *      (`snsMedia.xPostUrl` / `snsMedia.instagramPostUrl`) and customers
 *      repost it from their own account (X retweet intent / IG "add to
 *      story"). Concentrates engagement on a single post instead of
 *      scattering it across individual customer posts.
 *   2. Own-post compose — the customer writes their own post via each
 *      platform's compose intent, with common + store hashtags attached.
 *      Kept because a repost alone leaves nothing "of their own" on the
 *      customer's timeline.
 *
 * Open each platform's compose intent directly so the compose UX renders
 * correctly with hashtags, mentions, and the URL preview card. Avoid
 * Web Share API (`navigator.share`) for compose — on Windows desktop it
 * routes through the OS share sheet and degrades the payload to a bare URL.
 */

export type SharePlatform =
  | 'x'
  | 'facebook'
  | 'line'
  | 'whatsapp'
  | 'instagram'
  | 'x_repost'
  | 'instagram_story'
  | 'copy_link'
  | 'other'

export interface ShareResult {
  platform: SharePlatform
  attempted: boolean
}

/** App-wide hashtags (no `#`). Single source of truth — a hashtag only
 *  aggregates if every customer posts the exact same tag, so this is a
 *  constant rather than an i18n key (translated tags would fragment it). */
export const COMMON_HASHTAGS = ['YOKOSOPAY', 'VisitJapan']

/** UI-side limits for store custom hashtags (backend allows 10/50 as a
 *  looser safety net — see routers/stores.py `_sanitize_sns_media`). */
export const MAX_STORE_HASHTAGS = 5
export const MAX_STORE_HASHTAG_LEN = 30

export interface ShareTarget {
  /** Compose text. We pass the store name as-is so the post reads naturally
   *  regardless of the viewer's locale. */
  storeName: string
  /** Canonical store URL — passed as a separate `url` param on X so the post
   *  renders as a link card rather than inline text. */
  storeUrl: string
  /** Optional store-owned X handle. When present, X attributes the post
   *  (`via @handle`). */
  twitterHandle?: string
  /** Hashtags as normalized tokens (no `#`). Defaults to COMMON_HASHTAGS;
   *  callers pass `combineHashtags(store tags)` to append store tags. */
  hashtags?: string[]
  /** Curated image URL to attach when sharing — used by the Web Share API
   *  path on mobile so the user can post the image directly to Instagram
   *  Story / IG Post without leaving the share sheet. */
  imageUrl?: string
}

// ----------------------------------------------------------------
// Hashtag helpers (#69)
// ----------------------------------------------------------------

/** Parse free-text hashtag input (store settings) into normalized tokens.
 *  Accepts space / comma / `#` separated input, half- or full-width. */
export function parseHashtagsInput(raw: string): string[] {
  const tags: string[] = []
  const seen = new Set<string>()
  for (const token of raw.split(/[\s,、，#＃]+/u)) {
    const tag = token.trim().slice(0, MAX_STORE_HASHTAG_LEN)
    if (!tag || seen.has(tag.toLowerCase())) continue
    seen.add(tag.toLowerCase())
    tags.push(tag)
    if (tags.length >= MAX_STORE_HASHTAGS) break
  }
  return tags
}

/** Common tags first, then store tags, deduped case-insensitively. */
export function combineHashtags(storeTags?: string[]): string[] {
  const tags = [...COMMON_HASHTAGS]
  const seen = new Set(tags.map((t) => t.toLowerCase()))
  for (const raw of storeTags ?? []) {
    const tag = raw.trim().replace(/^[#＃]+/, '')
    if (!tag || seen.has(tag.toLowerCase())) continue
    seen.add(tag.toLowerCase())
    tags.push(tag)
  }
  return tags
}

/** X intent `hashtags=` format: comma-separated, no `#`. */
export function hashtagsToIntentParam(tags: string[]): string {
  return tags.join(',')
}

/** Human-readable format for compose text bodies: `#tag1 #tag2`. */
export function hashtagsToText(tags: string[]): string {
  return tags.map((t) => `#${t}`).join(' ')
}

// ----------------------------------------------------------------
// Official-post resolution (#69)
// ----------------------------------------------------------------

/** Extract the numeric tweet id from an X / Twitter post URL.
 *  Returns null for profile URLs, search URLs, or anything else. */
export function extractTweetId(url?: string): string | null {
  if (!url) return null
  const m = url.match(
    /^https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/[^/]+\/status(?:es)?\/(\d+)/,
  )
  return m ? m[1] : null
}

/** One-tap repost confirmation screen for a specific post.
 *  NOTE: intentionally `twitter.com` while compose uses `x.com` — the
 *  retweet intent is only reliably served on the legacy domain, whereas
 *  `x.com/intent/post` is the modern compose path. Do not "unify" these. */
export function buildRetweetIntentUrl(tweetId: string): string {
  return `https://twitter.com/intent/retweet?tweet_id=${encodeURIComponent(tweetId)}`
}

export interface OfficialPostUrls {
  xPostUrl?: string
  instagramPostUrl?: string
}

/** Resolve the store's official post URLs, falling back to the legacy
 *  single-URL field (`snsMedia.sharePostUrl`) by domain detection. The
 *  legacy field stops being written once the owner re-saves the SNS tab
 *  (StoreSettings rebuilds the whole snsMedia map), so this fallback is
 *  the read-time migration path — no Firestore backfill needed. */
export function resolveOfficialPostUrls(snsMedia?: {
  xPostUrl?: string
  instagramPostUrl?: string
  sharePostUrl?: string
}): OfficialPostUrls {
  const legacy = snsMedia?.sharePostUrl
  return {
    xPostUrl: snsMedia?.xPostUrl || (extractTweetId(legacy) ? legacy : undefined),
    instagramPostUrl:
      snsMedia?.instagramPostUrl || (isInstagramPostUrl(legacy) ? legacy : undefined),
  }
}

// ----------------------------------------------------------------
// Tier 1: official-post amplification
// ----------------------------------------------------------------

/** Open X's one-tap repost confirmation for the store's official post. */
export function shareRepostOnX(xPostUrl?: string): ShareResult {
  if (typeof window === 'undefined') return { platform: 'other', attempted: false }
  const tweetId = extractTweetId(xPostUrl)
  if (!tweetId) return { platform: 'other', attempted: false }
  window.open(buildRetweetIntentUrl(tweetId), '_blank', 'noopener,noreferrer')
  return { platform: 'x_repost', attempted: true }
}

/** Open the store's official Instagram post — Instagram has no web compose
 *  or repost intent, so the user reshares it in-app ("add to story", the
 *  3-step flow surfaced by `InstagramStoryHintModal`). */
export function shareToInstagramPost(postUrl?: string): ShareResult {
  if (typeof window === 'undefined') return { platform: 'other', attempted: false }
  if (!postUrl || !isInstagramPostUrl(postUrl)) {
    return { platform: 'other', attempted: false }
  }
  window.open(postUrl, '_blank', 'noopener,noreferrer')
  return { platform: 'instagram_story', attempted: true }
}

// ----------------------------------------------------------------
// Tier 2: own-post compose
// ----------------------------------------------------------------

export function shareToX(t: ShareTarget): ShareResult {
  if (typeof window === 'undefined') return { platform: 'other', attempted: false }

  const params = new URLSearchParams({
    text: t.storeName,
    url: t.storeUrl,
    hashtags: hashtagsToIntentParam(t.hashtags ?? COMMON_HASHTAGS),
  })
  const via = t.twitterHandle?.replace(/^@/, '')
  if (via) params.set('via', via)

  window.open(`https://x.com/intent/post?${params.toString()}`, '_blank', 'noopener,noreferrer')
  return { platform: 'x', attempted: true }
}

export function shareToFacebook(t: ShareTarget): ShareResult {
  if (typeof window === 'undefined') return { platform: 'other', attempted: false }

  // sharer.php takes a single `u` param. Facebook fetches OG meta tags
  // from the URL to render a preview card — title / image / description
  // come from the destination page's <meta property="og:..." /> tags.
  const params = new URLSearchParams({ u: t.storeUrl })
  window.open(`https://www.facebook.com/sharer/sharer.php?${params.toString()}`, '_blank', 'noopener,noreferrer')
  return { platform: 'facebook', attempted: true }
}

/** Detect whether a URL points to an Instagram post / reel / IGTV. We check
 *  hostname rather than path so subtle variants (`instagram.com/p/...` vs
 *  `www.instagram.com/reel/...`) all match. */
export function isInstagramPostUrl(url?: string): boolean {
  if (!url) return false
  try {
    const u = new URL(url)
    return u.hostname === 'www.instagram.com' || u.hostname === 'instagram.com'
  } catch {
    return false
  }
}

/** Try to share a curated image directly via the Web Share API. Works on
 *  iOS Safari / Android Chrome where `navigator.canShare({files})` is
 *  supported — the OS share sheet opens with the image attached, and the
 *  user can pick Instagram (or any app) to forward it to. From there
 *  Instagram opens the Story camera with the image as background.
 *
 *  Returns true on success (share sheet opened), false on any failure
 *  (no API support, fetch error, user cancelled). Caller decides the
 *  fallback (the button is hidden entirely when no curated image exists).
 */
export async function shareImageViaWebShare(t: ShareTarget): Promise<boolean> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false
  }
  if (!t.imageUrl) return false

  try {
    const response = await fetch(t.imageUrl)
    if (!response.ok) return false
    const blob = await response.blob()
    const ext = blob.type.split('/')[1]?.split('+')[0] || 'jpg'
    const file = new File([blob], `share.${ext}`, { type: blob.type })

    if (typeof navigator.canShare !== 'function' || !navigator.canShare({ files: [file] })) {
      return false
    }

    await navigator.share({
      title: t.storeName,
      text: `${t.storeName}\n${hashtagsToText(t.hashtags ?? COMMON_HASHTAGS)}`,
      files: [file],
    })
    return true
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') {
      // User dismissed the share sheet — treat as completed (we don't want
      // to surface an error because they intentionally cancelled).
      return true
    }
    return false
  }
}

export function shareToWhatsApp(t: ShareTarget): ShareResult {
  if (typeof window === 'undefined') return { platform: 'other', attempted: false }

  // wa.me with no number opens the contact picker (web app or installed
  // mobile app). The `text` param prefills the compose so the recipient
  // gets store name + URL on the first message. No hashtags — they carry
  // no function in messengers and read as noise.
  const text = `${t.storeName}\n${t.storeUrl}`
  const params = new URLSearchParams({ text })
  window.open(`https://wa.me/?${params.toString()}`, '_blank', 'noopener,noreferrer')
  return { platform: 'whatsapp', attempted: true }
}

export function shareToLine(t: ShareTarget): ShareResult {
  if (typeof window === 'undefined') return { platform: 'other', attempted: false }

  const text = `${t.storeName} @ YOKOSO PAY\n${t.storeUrl}`
  window.open(`https://line.me/R/share?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer')
  return { platform: 'line', attempted: true }
}

export function buildShareUrl(storeId?: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://yokosopay.com'
  return storeId ? `${base}/stores/${storeId}` : base
}
